import React from 'react'
import { NavLink } from 'react-router-dom'
import { Icon, type IconName } from './Icon'
import { TenonLogo } from './TenonLogo'

export type NavItem = {
  id: string
  label: string
  to: string
  icon: IconName
  count?: string | number
  tone?: 'warn' | 'err'
}

export type NavGroup = {
  group: string
  items: NavItem[]
}

export const HUB_NAV: NavGroup[] = [
  {
    group: 'Operate',
    items: [
      { id: 'mission-control', label: 'Mission Control', to: '/mission-control', icon: 'zap' },
      { id: 'overview',  label: 'Overview',  to: '/overview',  icon: 'layout-dashboard' },
      { id: 'business',  label: 'Business',  to: '/business',  icon: 'trending-up' },
      { id: 'sales',     label: 'Sales',     to: '/sales',     icon: 'dollar-sign' },
      { id: 'customers', label: 'Customers', to: '/customers', icon: 'users' },
      { id: 'tenants',   label: 'Tenants',   to: '/tenants',   icon: 'building-2' },
      { id: 'support',   label: 'Support',   to: '/support',   icon: 'headphones' },
      { id: 'release-notes', label: 'Release Notes', to: '/release-notes', icon: 'file-text' },
    ],
  },
  {
    group: 'Platform',
    items: [
      { id: 'engineering', label: 'Engineering', to: '/engineering', icon: 'wrench' },
      { id: 'agents',      label: 'Agents',      to: '/agents',      icon: 'cpu' },
      { id: 'emails',      label: 'Emails',      to: '/emails',      icon: 'mail' },
      { id: 'settings',    label: 'Settings',    to: '/settings',    icon: 'settings' },
    ],
  },
]

export function Sidebar({
  admin,
  counts,
}: {
  admin?: { email: string; name: string } | null
  counts?: Partial<Record<string, { count: number; tone?: 'warn' | 'err' }>>
}) {
  const initials = (admin?.name || admin?.email || '?')
    .split(/[ @]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('') || '?'

  return (
    <aside className="sidebar">
      <div className="brand">
        <TenonLogo height={20} />
        <span style={{
          fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
          letterSpacing: '0.08em', fontWeight: 600, marginLeft: -2,
        }}>
          Hub
        </span>
      </div>

      {HUB_NAV.map((g) => (
        <React.Fragment key={g.group}>
          <div className="group-label">{g.group}</div>
          {g.items.map((i) => {
            const c = counts?.[i.id]
            return (
              <NavLink
                key={i.id}
                to={i.to}
                end={i.to === '/overview'}
                className="nav-item"
                data-tone={c?.tone}
                style={({ isActive }) => isActive
                  ? { background: 'var(--accent-soft)', color: 'var(--text)', boxShadow: 'inset 2px 0 0 var(--accent)' }
                  : undefined}
              >
                <Icon name={i.icon} size={14} className="ic" />
                <span>{i.label}</span>
                {c && c.count > 0 && <span className="count">{c.count}</span>}
              </NavLink>
            )
          })}
        </React.Fragment>
      ))}

      <div className="user-card">
        <div className="avatar">{initials}</div>
        <div style={{ lineHeight: 1.2, minWidth: 0 }}>
          <div style={{ color: 'var(--text)', fontSize: 12 }}>{admin?.name || admin?.email || 'Admin'}</div>
          {admin?.email && (
            <div style={{ fontSize: 10.5, color: 'var(--text-dim)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {admin.email}
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}
